import flattenDeep from 'lodash/fp/flattenDeep'
import z from 'zod'

const DISABLED_CLAIMS = [
    'given_name',
    'family_name',
    'name',
    'authmethod',
    'auth_time',
    'opus_contact_id',
]

export const claimTypesEnum = z.enum(['opus_concept', 'opus_work', 'opus_contacts', 'opus_asset'])
export type AuthPermission = z.infer<typeof claimTypesEnum>

export type UserClaim = { type?: string | null; value?: string | null }

export type ClaimFieldValues = Record<string, any>

export const claimsToFieldValues = (claims: UserClaim[]): ClaimFieldValues => {
    return claims.reduce<ClaimFieldValues>((acc, claim) => {
        const { type, value } = claim
        if (type == null || value == null) return acc

        const [claimType, claimTypeValue = value] = value.split('|')
        const [accessLevel, accessLevelIdentifier] = claimTypeValue.split(':')
        return {
            ...acc,
            [type]: {
                ...(acc[type] ?? {}),
                [claimType]: {
                    ...(acc[type]?.[claimType] ?? {}),
                    [accessLevel]: {
                        ...(acc[type]?.[claimType]?.[accessLevel] ?? {}),
                        [accessLevelIdentifier]: {
                            ...(acc[type]?.[claimType]?.[accessLevel]?.[accessLevelIdentifier] ??
                                {}),
                            value: true,
                        },
                    },
                },
            },
        }
    }, {})
}
export const fieldValuesToclaims = (fields: ClaimFieldValues): UserClaim[] =>
    flattenDeep(
        Object.keys(fields).map((field) =>
            Object.keys(fields[field]).map((typeKey) =>
                Object.keys(fields[field][typeKey]).map((accessLevel) =>
                    Object.keys(fields[field][typeKey][accessLevel]).map((accessLevelIdentifier) =>
                        fields[field][typeKey][accessLevel][accessLevelIdentifier].value
                            ? {
                                  type: field,
                                  value:
                                      typeKey === 'series'
                                          ? `${typeKey}`
                                          : `${typeKey}|${accessLevel}:${accessLevelIdentifier}`,
                              }
                            : false,
                    ),
                ),
            ),
        ),
    ).filter((claim): claim is { type: string; value: string } => Boolean(claim))

export const isClaimDisabled = (claim: { type?: string | null }) =>
    DISABLED_CLAIMS.includes(claim.type ?? '')
export const isOpusUser = (claims: UserClaim[]) =>
    claims.find((claim) => claim.type?.startsWith('opus_')) !== undefined
export const hasClaimType = (claimType: string, claims: UserClaim[]) =>
    claims.find((claim) => claim.type === claimType) !== undefined
export const getContactIds = (claims: UserClaim[]) =>
    claims
        .filter((claim) => claim.type === 'opus_contact_id')
        .map((claim) => claim.value)
        .filter((value): value is string => value != null)
